// High Level Finance — shared helpers (formatting, dates, buckets, tokens).
// Pure functions and constants only; no React here.

import * as XLSX from 'xlsx';

export const FONT = '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, system-ui, sans-serif';

// ---- design tokens (the Work Allocations / Finance-custom language, plus
// the two extra series colours this page needs: rose for Projected
// Expense, mint for Projected Income; `${color}14` alpha needs plain hex).
export const LIGHT = {
  page: '#f6f6f7', card: '#ffffff', fg: '#1c1c1f', muted: '#6c6c76', faint: '#a0a0a8',
  border: '#e8e8eb', surface: '#f3f3f5', primary: '#232327', primaryFg: '#ffffff',
  green: '#16a34a', amber: '#d9930b', red: '#dc2626', blue: '#1868db', grey: '#5e6c84',
  teal: '#0e9f6e', cyan: '#0891b2', purple: '#7c5cd6', rose: '#e0788f', mint: '#5cc9a1', orange: '#f28c28',
  lightBlue: '#5eb3f0', pink: '#f28fb4',
  shadowXs: '0 1px 2px -1px rgba(17,17,26,0.06), 0 1px 1px rgba(17,17,26,0.04)',
  shadowSm: '0 1px 2px -1px rgba(17,17,26,0.06), 0 3px 8px -2px rgba(17,17,26,0.07)',
  shadowMd: '0 4px 8px -3px rgba(17,17,26,0.08), 0 12px 28px -6px rgba(17,17,26,0.14)',
};
export const DARK = {
  page: '#161618', card: '#1f1f23', fg: '#f5f5f7', muted: '#a1a1aa', faint: '#6b6b74',
  border: '#323238', surface: '#26262b', primary: '#e8e8ec', primaryFg: '#1a1a1d',
  green: '#4ade80', amber: '#fbbf24', red: '#f87171', blue: '#60a5fa', grey: '#94a3b8',
  teal: '#34d399', cyan: '#22d3ee', purple: '#a78bfa', rose: '#f4a3b5', mint: '#86efc4', orange: '#fba94c',
  lightBlue: '#7cc4f5', pink: '#f6a8c5',
  shadowXs: '0 1px 2px rgba(0,0,0,0.45)',
  shadowSm: '0 1px 2px rgba(0,0,0,0.45), 0 3px 8px -2px rgba(0,0,0,0.4)',
  shadowMd: '0 4px 12px rgba(0,0,0,0.5), 0 12px 28px -6px rgba(0,0,0,0.55)',
};

// A categorical palette for the per-supplier chart (one colour per series;
// wraps after 14). Readable on both themes.
export const PALETTE = ['#e0432c', '#5b5a4a', '#c084fc', '#f7d38a', '#22d3ee', '#7c3aed', '#f97316', '#6b7280', '#facc15', '#f87171', '#d8b4fe', '#eab308', '#a855f7', '#0ea5e9'];

// ---- dates ----------------------------------------------------------------
export const pad = (n) => String(n).padStart(2, '0');
export const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayISO = () => isoOf(new Date());
export const yearFromISO = () => `${new Date().getFullYear()}-01-01`;
export const yearToISO = () => `${new Date().getFullYear()}-12-31`;
export const parseYmd = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d); };
export const fmtDue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
};
// 'YYYY-MM' → "Jan 2026"
export const fmtMonth = (ym) => {
  if (!ym || ym === NONE) return 'None';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
// Every 'YYYY-MM' from a to b inclusive (both 'YYYY-MM' or ISO dates).
export const monthsBetween = (a, b) => {
  if (!a || !b) return [];
  let [y, m] = a.slice(0, 7).split('-').map(Number);
  const [y2, m2] = b.slice(0, 7).split('-').map(Number);
  const out = [];
  while (y < y2 || (y === y2 && m <= m2)) {
    out.push(`${y}-${pad(m)}`);
    m += 1; if (m > 12) { m = 1; y += 1; }
    if (out.length > 600) break;                       // 50 years — a runaway guard
  }
  return out;
};

// ---- time buckets for the charts (the Work Allocations Resolution Graph
// pattern, plus Quarter). A bucket is keyed by its START date (ISO);
// 'None' is the leading bucket for items with no due date. -------------
export const NONE = 'None';
export const BUCKETS = [
  { key: 'day', label: 'Per Day' },
  { key: 'week', label: 'Per Week' },
  { key: 'month', label: 'Per Month' },
  { key: 'quarter', label: 'Per Quarter' },
];
const addDaysD = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const startOfWeek = (date) => { const d = new Date(date); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); return d; };
export const bucketStartOf = (iso, bucket) => {
  if (!iso) return NONE;
  const d = parseYmd(iso);
  if (bucket === 'day') return String(iso).slice(0, 10);
  if (bucket === 'week') return isoOf(startOfWeek(d));
  if (bucket === 'quarter') return `${d.getFullYear()}-${pad(Math.floor(d.getMonth() / 3) * 3 + 1)}-01`;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
};
export const nextBucketStart = (iso, bucket) => {
  const d = parseYmd(iso);
  if (bucket === 'day') return isoOf(addDaysD(d, 1));
  if (bucket === 'week') return isoOf(addDaysD(d, 7));
  if (bucket === 'quarter') return isoOf(new Date(d.getFullYear(), d.getMonth() + 3, 1));
  return isoOf(new Date(d.getFullYear(), d.getMonth() + 1, 1));
};
export const bucketLabel = (start, bucket) => {
  if (start === NONE) return 'None';
  const d = parseYmd(start);
  if (bucket === 'day' || bucket === 'week') return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' });
  if (bucket === 'quarter') return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
export function niceMax(v) {
  const hi = Math.max(1, v) * 1.08;
  const mag = 10 ** Math.floor(Math.log10(hi));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s * 5 >= hi) || mag;
  return Math.ceil(hi / step) * step;
}

// ---- work-item accessors --------------------------------------------------
export const normName = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
export const typeOf = (it) => it?.fields?.issuetype?.name || '—';
export const statusName = (it) => it?.fields?.status?.name || '—';
export const statusCat = (it) => String(it?.fields?.status?.statusCategory?.key || it?.fields?.status?.statusCategory?.name || '').toLowerCase();
export const dueOf = (it) => it?.fields?.duedate || '';
export const projOf = (it) => (it?.key || '').split('-')[0];
export const assigneeOf = (it) => {
  const a = it?.fields?.assignee;
  return (a && (a.displayName || a.name || a.emailAddress)) || '';
};
export const numOf = (it, fieldId) => {
  if (!fieldId) return null;
  const v = it?.fields?.[fieldId];
  const n = typeof v === 'number' ? v : (v != null && v !== '' ? Number(v) : NaN);
  return Number.isFinite(n) ? n : null;
};
// An option / user / string field → its display text; an ARRAY of options
// joins the values ("A, B") so a multi-supplier order counts once, not twice.
export const optOf = (it, fieldId) => {
  if (!fieldId) return '';
  const v = it?.fields?.[fieldId];
  const one = (x) => (x && (x.value || x.name || x.displayName)) || (typeof x === 'string' ? x : '');
  if (Array.isArray(v)) return v.map(one).filter(Boolean).join(', ');
  return one(v);
};

// ---- money & hours --------------------------------------------------------
// ZAR money: ALWAYS "R #,##0.00" — 2 decimals, comma thousands.
export const fmtR = (n) => {
  if (n == null) return '';
  return 'R ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
export const fmtH = (n) => (n == null ? '' : n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 }));
export const round2 = (n) => Math.round(n * 100) / 100;
export const round1 = (n) => Math.round(n * 10) / 10;
export const EXCEL_MONEY_FMT = 'R #,##0.00';
// Stamp a number format on every numeric cell of the given columns.
export const setColFormat = (ws, cols, fmt = EXCEL_MONEY_FMT) => {
  if (!ws['!ref']) return;
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let R = 1; R <= range.e.r; R++) {
    cols.forEach((C) => {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && typeof cell.v === 'number') cell.z = fmt;
    });
  }
};
export const writeSheet = (aoa, cols, moneyCols, sheetName, fileStem, fmt) => {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols;
  setColFormat(ws, moneyCols, fmt);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, `${fileStem}-${todayISO()}.xlsx`);
};

// Click-to-sort: same column toggles ASC/DESC; a new column starts DESC
// for numbers, ASC for text.
export const nextSort = (cur, key, numeric) =>
  cur && cur.key === key
    ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: numeric ? 'desc' : 'asc' };
